import React, { useState } from 'react';
import {
  useUnenrollStudentMutation,
  useEndEnrollmentMutation,
  useCancelBookingMutation,
  useTransferStudentMutation,
} from '../../store/api/localApi';
import RoleGate from '../../components/RoleGate';
import EditDaysModal from './EditDaysModal';
import ConfirmDialog from '../../components/ConfirmDialog';
import {
  ArrowRightCircleIcon,
  CalendarDaysIcon,
  ClockIcon,
  PencilSquareIcon,
  NoSymbolIcon,
  UserGroupIcon,
  UserPlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { studentDisplayName } from '../../utils/student';
import { todayIso, formatDayMonth, formatWeekdays } from '../../utils/terms';
import type { ClassRosterStudent } from '../../types';

export interface EnrolledStudentsSectionProps {
  classId: string;
  /** The class's name, for the Remove confirmation and the day picker's copy. */
  className: string;
  /** The class's location, for today's session (Edit days' From date). */
  locationId: string;
  /**
   * Roster rows. `daysOfWeek` are today's days (Edit days starts from them);
   * `endsToday` / `lastDay` the last day; `startsOn` a child who starts later;
   * `upcoming` a change of days scheduled for a later date.
   */
  students: Array<
    Pick<
      ClassRosterStudent,
      'id' | 'firstName' | 'lastName' | 'daysOfWeek' | 'endsToday' | 'lastDay' | 'startsOn' | 'upcoming'
    > & { grade?: string }
  >;
  count: number;
  isAdmin: boolean;
  onEnroll: () => void;
  /** Other classes at this location, for the transfer target picker. */
  transferTargets?: Array<{ id: string; name: string }>;
  /**
   * Bookings for terms that have not started. Rendered as their own subsection
   * rather than mixed into the roster: these children are not in the class
   * today, so counting them among the enrolled would inflate the headcount and
   * put them on the attendance register. Cancelling one DELETES the booking —
   * there is no attendance history to keep for a term never attended.
   */
  upcoming?: Array<{
    studentId: string;
    firstName?: string;
    lastName?: string;
    grade?: string;
    termId: string;
    termName: string;
    termStartDate: string;
  }>;
}

type Pending =
  | { kind: 'unenroll'; id: string; name: string }
  | { kind: 'end'; id: string; name: string }
  | { kind: 'cancel'; id: string; name: string; termId: string }
  | { kind: 'transfer'; id: string; name: string };

const EnrolledStudentsSection: React.FC<EnrolledStudentsSectionProps> = ({
  classId,
  className,
  locationId,
  students,
  count,
  isAdmin,
  onEnroll,
  transferTargets = [],
  upcoming = [],
}) => {
  const [unenroll] = useUnenrollStudentMutation();
  const [endEnrollment, { isLoading: isEnding }] = useEndEnrollmentMutation();
  const [cancelBooking, { isLoading: isCancelling }] = useCancelBookingMutation();
  const [transferStudent, { isLoading: isTransferring }] = useTransferStudentMutation();

  const [pending, setPending] = useState<Pending | null>(null);
  const [endDate, setEndDate] = useState(todayIso());
  // The server's floor for a last day, once it has refused an earlier one.
  const [endDateMin, setEndDateMin] = useState<string | undefined>();
  // The term's last day, once the server has refused a last day outside it.
  const [endDateMax, setEndDateMax] = useState<string | undefined>();
  const [transferToClassId, setTransferToClassId] = useState('');
  const [error, setError] = useState('');
  const [editingDays, setEditingDays] = useState<{
    id: string;
    name: string;
    daysOfWeek: number[];
    startsOn?: string;
    upcoming?: { from: string; daysOfWeek: number[] };
  } | null>(null);

  const close = () => {
    setPending(null);
    setError('');
    setEndDateMin(undefined);
    setEndDateMax(undefined);
  };

  const openEnd = (id: string, name: string) => {
    setEndDate(todayIso());
    setError('');
    setPending({ kind: 'end', id, name });
  };

  const openTransfer = (id: string, name: string) => {
    setTransferToClassId(transferTargets[0]?.id ?? '');
    setError('');
    setPending({ kind: 'transfer', id, name });
  };

  const confirm = async () => {
    if (!pending) return;
    setError('');
    try {
      if (pending.kind === 'unenroll') {
        await unenroll({ classId, studentId: pending.id }).unwrap();
      } else if (pending.kind === 'end') {
        const ended = await endEnrollment({ classId, studentId: pending.id, endDate }).unwrap();
        // The server reports the last day the row really has: when it left the
        // row alone (a booking cancelled before it started is never revived),
        // say so instead of closing as if the date had been set.
        if (ended?.changed === false && ended?.endDate && ended.endDate !== endDate) {
          setError(
            `This enrolment already ends on ${formatDayMonth(ended.endDate)}. Nothing changed.`
          );
          return;
        }
      } else if (pending.kind === 'cancel') {
        await cancelBooking({ classId, studentId: pending.id, termId: pending.termId }).unwrap();
      } else if (pending.kind === 'transfer') {
        if (!transferToClassId) {
          setError('Pick a class to transfer to.');
          return;
        }
        await transferStudent({
          fromClassId: classId,
          toClassId: transferToClassId,
          studentId: pending.id,
        }).unwrap();
      }
      close();
    } catch (err: any) {
      if (err?.data?.error === 'END_DATE_TOO_EARLY' && err?.data?.earliest) {
        setError(`The last day can be ${formatDayMonth(err.data.earliest)} at the earliest.`);
        setEndDateMin(err.data.earliest);
        setEndDate(err.data.earliest);
      } else if (
        err?.data?.error === 'END_DATE_OUTSIDE_TERM' &&
        err?.data?.termStartDate &&
        err?.data?.termEndDate
      ) {
        const { termStartDate, termEndDate } = err.data as {
          termStartDate: string;
          termEndDate: string;
        };
        const start = formatDayMonth(termStartDate);
        const end = formatDayMonth(termEndDate);
        if (pending.kind === 'transfer') {
          // A transfer's last day here is today, which the admin cannot pick:
          // the child's row is in a term that is not running today.
          setError(
            `This child's place here is in the term running ${start} to ${end}, so they cannot be transferred today.`
          );
          return;
        }
        setError(`The last day must be between ${start} and ${end}.`);
        // Keep a floor the server already gave if it is the stricter one.
        const min = endDateMin && endDateMin > termStartDate ? endDateMin : termStartDate;
        setEndDateMin(min);
        setEndDateMax(termEndDate);
        if (endDate < min) setEndDate(min);
        else if (endDate > termEndDate) setEndDate(termEndDate);
      } else if (err?.data?.error === 'END_ALREADY_PASSED' && err?.data?.endDate) {
        setError(
          `This enrolment already ended on ${formatDayMonth(
            err.data.endDate
          )}. Enrol the child again instead.`
        );
      } else if (err?.data?.error === 'ALREADY_ENROLLED' && err?.data?.until) {
        const target = transferTargets.find((cls) => cls.id === transferToClassId);
        setError(
          `${pending.name} is already in ${target?.name ?? 'that class'} until ${formatDayMonth(
            err.data.until
          )}. Nothing changed.`
        );
      } else if (err?.data?.error === 'CAPACITY_EXCEEDED') {
        setError('The target class is at capacity on one or more of those days. Nothing changed.');
      } else if (err?.data?.error === 'EFFECTIVE_FROM_AFTER_TERM' && err?.data?.termEndDate) {
        setError(`This term ends on ${formatDayMonth(err.data.termEndDate)}.`);
      } else {
        setError(err?.data?.message || err?.data?.error || 'Action failed');
      }
    }
  };

  const busy = isEnding || isCancelling || isTransferring;

  const dialogCopy = (): { title: string; message: string; confirmLabel: string } => {
    switch (pending?.kind) {
      case 'end':
        return {
          title: 'End Enrollment',
          message: `Set ${pending.name}'s last day in this class.`,
          confirmLabel: 'End enrollment',
        };
      case 'cancel':
        return {
          title: 'Cancel Booking',
          message: `Cancel ${pending.name}'s booking for the upcoming term? The booking is removed entirely.`,
          confirmLabel: 'Cancel booking',
        };
      case 'transfer':
        return {
          title: 'Transfer Student',
          message: `Move ${pending.name} to another class. They leave this class today.`,
          confirmLabel: 'Transfer',
        };
      default:
        return {
          title: 'Remove Student',
          message: pending
            ? `Remove ${pending.name} from ${className}? They stay on today's register if today's session has already started. Their booking for later terms in this class is cancelled too.`
            : '',
          confirmLabel: 'Remove',
        };
    }
  };

  const copy = dialogCopy();

  return (
    <div className="bg-white shadow rounded-lg" data-testid="enrolled-students">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
            Enrolled Students ({count})
          </h3>
          <RoleGate when={isAdmin}>
            <button
              data-testid="enroll-student-btn"
              onClick={onEnroll}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              <UserPlusIcon className="h-4 w-4 mr-1" /> Enroll Student
            </button>
          </RoleGate>
        </div>

        {students.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {students.map((student) => {
              const name = studentDisplayName(student);
              return (
                <li key={student.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center">
                    <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-primary-600">
                        {student.firstName?.[0] ?? '?'}
                        {student.lastName?.[0] ?? ''}
                      </span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {name}
                        {student.endsToday && (
                          <span
                            data-testid={`enrollment-ends-today-${student.id}`}
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                          >
                            Last day today
                          </span>
                        )}
                        {student.lastDay && !student.endsToday && (
                          <span
                            data-testid={`enrollment-last-day-${student.id}`}
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                          >
                            Last day {formatDayMonth(student.lastDay)}
                          </span>
                        )}
                        {student.startsOn && (
                          <span
                            data-testid={`enrollment-starts-${student.id}`}
                            className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                          >
                            Starts {formatDayMonth(student.startsOn)}
                          </span>
                        )}
                      </p>
                      {student.grade && <p className="text-xs text-gray-500">{student.grade}</p>}
                      {student.upcoming && (
                        <p
                          data-testid={`enrollment-upcoming-${student.id}`}
                          className="text-xs text-gray-500"
                        >
                          {formatWeekdays(student.upcoming.daysOfWeek)} from{' '}
                          {formatDayMonth(student.upcoming.from)}
                        </p>
                      )}
                    </div>
                  </div>
                  <RoleGate when={isAdmin}>
                    <div className="flex items-center gap-1">
                      {/* A row ending today has no later day for new days to
                          apply to, so an edit could never show anywhere. The way
                          back for that child is Enrol, which reopens the row. */}
                      {!student.endsToday && (
                        <button
                          data-testid={`enrollment-edit-days-${student.id}`}
                          onClick={() =>
                            setEditingDays({
                              id: student.id,
                              name,
                              daysOfWeek: student.daysOfWeek ?? [],
                              startsOn: student.startsOn,
                              upcoming: student.upcoming,
                            })
                          }
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="Edit days"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        data-testid={`enrollment-end-${student.id}`}
                        onClick={() => openEnd(student.id, name)}
                        className="p-1 text-gray-400 hover:text-amber-600"
                        title="End enrollment"
                      >
                        <CalendarDaysIcon className="h-4 w-4" />
                      </button>
                      {transferTargets.length > 0 && (
                        <button
                          data-testid={`enrollment-transfer-${student.id}`}
                          onClick={() => openTransfer(student.id, name)}
                          className="p-1 text-gray-400 hover:text-primary-600"
                          title="Transfer to another class"
                        >
                          <ArrowRightCircleIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        data-testid={`unenroll-${student.id}`}
                        onClick={() => setPending({ kind: 'unenroll', id: student.id, name })}
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Remove"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </RoleGate>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No students enrolled yet</p>
        )}

        {upcoming.length > 0 && (
          <div className="mt-6 pt-4 border-t" data-testid="enrollment-upcoming-section">
            <h4 className="text-sm font-medium text-gray-900 flex items-center mb-2">
              <ClockIcon className="h-4 w-4 mr-2 text-gray-400" />
              Upcoming ({upcoming.length})
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Booked for a term that has not started. They are not on today&apos;s roster.
            </p>
            <ul className="divide-y divide-gray-200">
              {upcoming.map((booking) => {
                const name = studentDisplayName(booking);
                return (
                  <li
                    key={`${booking.studentId}-${booking.termId}`}
                    data-testid={`enrollment-upcoming-row-${booking.studentId}`}
                    className="flex items-center justify-between py-2.5"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{name}</p>
                      <p className="text-xs text-gray-500">
                        {booking.termName} — starts {booking.termStartDate}
                      </p>
                    </div>
                    <RoleGate when={isAdmin}>
                      <button
                        data-testid={`enrollment-cancel-booking-${booking.studentId}`}
                        onClick={() =>
                          setPending({
                            kind: 'cancel',
                            id: booking.studentId,
                            name,
                            termId: booking.termId,
                          })
                        }
                        className="p-1 text-gray-400 hover:text-red-500"
                        title="Cancel booking"
                      >
                        <NoSymbolIcon className="h-4 w-4" />
                      </button>
                    </RoleGate>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {editingDays && (
        <EditDaysModal
          classId={classId}
          className={className}
          locationId={locationId}
          studentId={editingDays.id}
          studentName={editingDays.name}
          currentDays={editingDays.daysOfWeek}
          startsOn={editingDays.startsOn}
          upcoming={editingDays.upcoming}
          onClose={() => setEditingDays(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!pending}
        onConfirm={confirm}
        onCancel={close}
        title={copy.title}
        message={copy.message}
        confirmLabel={copy.confirmLabel}
        variant={pending?.kind === 'transfer' ? 'default' : 'warning'}
        isLoading={busy}
      >
        {pending?.kind === 'end' && (
          <div className="mt-3">
            <label
              htmlFor="enrollment-end-date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last day
            </label>
            <input
              id="enrollment-end-date"
              data-testid="enrollment-end-date"
              type="date"
              value={endDate}
              min={endDateMin}
              max={endDateMax}
              onChange={(e) => setEndDate(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
        )}
        {pending?.kind === 'transfer' && (
          <div className="mt-3">
            <label
              htmlFor="enrollment-transfer-class"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Transfer to
            </label>
            <select
              id="enrollment-transfer-class"
              data-testid="enrollment-transfer-class"
              value={transferToClassId}
              onChange={(e) => setTransferToClassId(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {transferTargets.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {error && (
          <p className="mt-3 text-sm text-red-600" data-testid="enrollment-action-error">
            {error}
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default EnrolledStudentsSection;
